import { useEffect, useState } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { BadgePercent, X } from 'lucide-react'
import { useCampaignBanner } from '../hooks/useCampaignBanner'
import { Badge } from '@/components/ui/badge'

function dismissedKey(campaignId: string): string {
  return `campaign-banner-dismissed-${campaignId}`
}

export function CampaignBanner() {
  const routerState = useRouterState()
  const isAdminPath = routerState.location.pathname.startsWith('/admin')

  const { data: banner } = useCampaignBanner()
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (!banner) return
    setDismissed(localStorage.getItem(dismissedKey(banner.id)) === '1')
  }, [banner])

  if (isAdminPath || !banner || dismissed) return null

  const text = banner.bannerText || banner.name
  const isScheduled = banner.status === 'scheduled' && !!banner.startsAt

  const handleDismiss = () => {
    localStorage.setItem(dismissedKey(banner.id), '1')
    setDismissed(true)
  }

  return (
    <div className="relative">
      {/* Thin accent line — a quieter attention cue than a solid color bar. */}
      <div className="h-0.5 w-full bg-gradient-to-r from-accent/40 via-primary to-accent/40" />

      <div className="flex items-center gap-3 border-b border-[var(--color-border)] bg-[var(--color-bg-primary)]/80 px-4 py-2.5 text-sm backdrop-blur-xl">
        <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-accent text-primary-foreground shadow-[0_0_16px_var(--color-primary-shadow)]">
          <BadgePercent className="h-3.5 w-3.5" />
        </span>

        <div className="flex flex-1 flex-wrap items-center justify-center gap-x-2 gap-y-0.5 text-center">
          {isScheduled && (
            <Badge variant="warning">
              Starts {new Date(banner.startsAt!).toLocaleDateString()}
            </Badge>
          )}
          <span className="font-medium text-[var(--color-text-primary)]">
            {text}
          </span>
          {banner.linkedPostSlug ? (
            <Link
              to="/blogs/$slug"
              params={{ slug: banner.linkedPostSlug }}
              className="rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 font-bold text-primary transition-all duration-300 ease-brisk hover:border-primary/50 hover:shadow-[0_0_16px_var(--color-primary-shadow)] active:scale-[0.97]"
            >
              Learn more →
            </Link>
          ) : (
            banner.bannerLinkUrl && (
              <a
                href={banner.bannerLinkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 font-bold text-primary transition-all duration-300 ease-brisk hover:border-primary/50 hover:shadow-[0_0_16px_var(--color-primary-shadow)] active:scale-[0.97]"
              >
                Learn more →
              </a>
            )
          )}
        </div>

        {banner.dismissible && (
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss banner"
            className="flex-shrink-0 rounded-full p-1 text-[var(--color-text-tertiary)] transition-all duration-300 ease-brisk hover:bg-[var(--color-control-surface-hover)] hover:text-[var(--color-text-primary)] active:scale-[0.97]"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  )
}
